/**
 * shipping-address.ts -- lengdegrensen Posten-roboten moeter i Postens eget
 * bestillingsskjema.
 *
 * KILDE (ORD-218, Congregatio Forensis i Tromsoe, 10.9.2026): leveringsadressen
 * «Universitetet i Tromsø Teorifagsbyggets Hus 4, 3. etasje» er 56 tegn.
 * Postens bestillingsskjema avviste den med
 *
 *     «Adressen kan ikke inneholde mer enn 50 tegn. Bruk adresselinje 2.»
 *
 * Roboten prover tre ganger og gir opp; leddet ble `failed` etter at Fredrik
 * hadde trykket «Book» og ventet. Ingenting tidligere i kjeden sa fra:
 * ordreskjemaet tok imot 56 tegn, kontrakten ble signert, og ordren stod med
 * groenn status i fraktbookingkoeen.
 *
 * Vi HAR ingen adresselinje 2 aa legge resten i -- verken todosystem eller
 * posten-manager har feltet (posten-manager sender `Address2: null` fast til
 * Proteria). Aa innfoere den gjennom hele kjeden er en egen jobb. Inntil da er
 * den eneste aerlige loesningen aa fange lengden FOER bookingen: i skjemaet,
 * i fraktbookingkoeen, og til slutt i payloaden.
 *
 * KUN ADRESSEN har en grense her. Mottakernavn og poststed har vi ingen
 * observert grense for, og posten-manager validerer heller ingen (se
 * `validatePayload` i lib/todosystemCreateAndBook.ts: bare «ikke tom» og
 * 4 siffer postnummer). Aa gjette et tall for navn/poststed ville vaert aa
 * blokkere ordrer paa en regel vi ikke vet finnes. Dukker det opp en ekte
 * avvisning fra Posten paa et av dem, hoerer grensen hjemme her, med samme
 * type kildehenvisning som denne.
 */

/** Postens bestillingsskjema: maks 50 tegn i adressefeltet (ORD-218 10.9.2026). */
export const POSTEN_MAX_ADDRESS_LENGTH = 50;

export interface ShippingAddressLengthProblem {
  /** Trimmet lengde -- det er den Posten faktisk teller. */
  length: number;
  max: number;
  message: string;
}

/**
 * Trimmet lengde. Baade `buildShippingBookingPayload` (shared/booking-recipient)
 * og posten-managers `validatePayload` trimmer foer de sender videre, saa det
 * er den trimmede strengen som havner i Postens skjema. Ledende/etterfoelgende
 * mellomrom skal derfor ikke kunne utloese en sperre.
 */
export function shippingAddressLength(address: string | null | undefined): number {
  return String(address ?? "").trim().length;
}

/**
 * `null` = adressen er innenfor grensen (tom adresse er ikke for lang -- at den
 * MANGLER er en annen sak, og har sin egen sperre i order-readiness).
 *
 * `label` lar samme regel snakke om riktig felt: «Leveringsadressen» i
 * fraktkoeen og i leveringsboksen, «Kundeadressen» paa kundens hovedadresse.
 */
export function checkShippingAddressLength(
  address: string | null | undefined,
  options?: { label?: string; max?: number },
): ShippingAddressLengthProblem | null {
  const max = options?.max ?? POSTEN_MAX_ADDRESS_LENGTH;
  const length = shippingAddressLength(address);
  if (length <= max) return null;
  const label = options?.label ?? "Leveringsadressen";
  return {
    length,
    max,
    message: `${label} er ${length} tegn – Posten godtar maks ${max}. Kort den ned.`,
  };
}
